import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Globe,
    Plus,
    Shield,
    Activity,
    RefreshCcw,
    ChevronRight,
    CheckCircle,
    XCircle,
    Clock,
    Lock,
    AlertCircle,
    Copy,
    Server,
    Cpu,
    Database,
    Zap,
    Trash2
} from 'lucide-react';
import adminService from '../../services/adminService';

/* ─────────────────────────────────────────────
   Status Badge — matches the pill used in UserManagement
───────────────────────────────────────────── */
const STATUS_CFG = {
    VERIFIED: { label: 'Verified', color: 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20', icon: CheckCircle },
    PENDING: { label: 'Pending', color: 'bg-amber-500/10  text-amber-500  border border-amber-500/20', icon: Clock },
    FAILED: { label: 'Failed', color: 'bg-red-500/10    text-red-500    border border-red-500/20', icon: XCircle },
};

const DNS_DISPLAY_NAMES = {
    'NONE': 'Standard',
    'CLOUDFLARE': 'Cloudflare',
    'AWS_ROUTE53': 'AWS Route 53',
    'GOOGLE_CLOUD': 'Google Cloud',
    'AZURE_DNS': 'Azure DNS',
    'DIGITALOCEAN': 'DigitalOcean',
    'VERCEL': 'Vercel',
    'GODADDY': 'GoDaddy',
    'NAMECHEAP': 'Namecheap',
    'PORKBUN': 'Porkbun',
    'VULTR': 'Vultr',
    'LINODE': 'Linode',
    'BLUEHOST': 'Bluehost',
    'HOSTGATOR': 'HostGator'
};

function StatusPill({ status }) {
    const cfg = STATUS_CFG[status] || STATUS_CFG.PENDING;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[9px] font-extrabold uppercase tracking-widest ${cfg.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status === 'VERIFIED' ? 'bg-emerald-500' : status === 'FAILED' ? 'bg-red-500' : 'bg-amber-500'}`} />
            {cfg.label}
        </span>
    );
}

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
export default function DomainManagement() {
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // domain to delete
    const [deleting, setDeleting] = useState(false);
    const [activeTab, setActiveTab] = useState('Overview');
    const [records, setRecords] = useState([]);
    const [copied, setCopied] = useState(null);
    const TENANT = "88888888-4444-4444-4444-121212121212";

    useEffect(() => { fetchDomains(); }, []);

    async function fetchDomains() {
        setLoading(true);
        try {
            const data = await adminService.getDomains(TENANT);
            setDomains(data);
        } catch (e) {
            console.error("Domain fetch failed:", e);
            setDomains([
                { id: 1, name: 'xevyte.co', status: 'VERIFIED', syncMode: 'MANUAL', riskScore: 45, dnsProvider: 'NONE' },
                { id: 2, name: 'email-ingress.io', status: 'PENDING', syncMode: 'AUTOMATIC', riskScore: 12, dnsProvider: 'CLOUDFLARE' },
            ]);
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete() {
        if (!deleteConfirm) return;
        setDeleting(true);
        try {
            await adminService.deleteDomain(deleteConfirm.id);
            setDeleteConfirm(null);
            if (selected?.id === deleteConfirm.id) setDrawerOpen(false);
            fetchDomains();
        } catch (e) {
            alert(`Failed to decommission domain: ${e.message}`);
        } finally {
            setDeleting(false);
        }
    }

    async function openDrawer(domain) {
        setSelected(domain);
        setActiveTab('Overview');
        setDrawerOpen(true);
        try {
            const data = await adminService.getDomainRecords(domain.id);
            setRecords(data);
        } catch {
            setRecords([
                { id: 1, recordType: 'TXT', recordName: '@', expectedValue: 'xemail-verify=8j2hb-9k3L-qp01-zz92', status: 'VERIFIED' },
                { id: 2, recordType: 'MX', recordName: '@', expectedValue: 'mta1.xevyte.co', status: 'PENDING' },
                { id: 3, recordType: 'CNAME', recordName: 'mail', expectedValue: 'ghsc.edge.xemail.io', status: 'VERIFIED' },
            ]);
        }
    }

    function copy(text, id) {
        navigator.clipboard.writeText(text);
        setCopied(id);
        setTimeout(() => setCopied(null), 2000);
    }

    /* ── render ─────────────────────────────── */
    return (
        <div className="space-y-8 animate-fade-in pb-10">

            {/* ── PAGE HEADER — same pattern as AdminDashboard ── */}
            <header className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight underline decoration-[#4f46e5]/30 decoration-4 underline-offset-8 uppercase tracking-wider">
                        Domain Management
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium">
                        Provision and monitor email ingress domains across your network.
                    </p>
                </div>
                <div className="flex items-center space-x-3">
                    <div className="hidden lg:flex items-center px-4 py-2 rounded-xl bg-[#4f46e5]/5 border border-[#4f46e5]/10 text-[10px] font-bold text-[#4f46e5] uppercase tracking-widest animate-pulse">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#4f46e5] mr-2" />
                        Registries Active
                    </div>
                    <button
                        onClick={fetchDomains}
                        className="p-3 enterprise-card bg-[rgb(var(--card))] hover:scale-105 active:scale-95 text-slate-500"
                    >
                        <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button
                        onClick={() => setModalOpen(true)}
                        className="flex items-center gap-2 px-5 py-2.5 bg-[#4f46e5] hover:bg-[#3558cc] active:scale-95 text-white text-sm font-bold rounded-xl shadow-sm shadow-[#4f46e5]/25 transition-all duration-200"
                    >
                        <Plus size={18} />
                        Add Domain
                    </button>
                </div>
            </header>

            {/* ── STAT CARDS — same layout as AdminDashboard ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { title: 'Total Domains', value: domains.length, icon: <Globe size={24} />, color: 'blue', sub: 'Managed FQDNs' },
                    { title: 'System Health', value: '100%', icon: <Shield size={24} />, color: 'emerald', sub: 'Active Shield' },
                    { title: 'Latency P99', value: '12ms', icon: <Zap size={24} />, color: 'amber', sub: 'MTA Pressure' },
                    { title: 'Uptime', value: '99.9%', icon: <Activity size={24} />, color: 'blue', sub: 'Traffic Score' },
                ].map((card, i) => (
                    <div key={i} className="enterprise-card p-6 group hover:-translate-y-1">
                        <div className="flex justify-between items-start mb-4">
                            <div className={`p-4 rounded-2xl group-hover:scale-110 transition-transform ${card.color === 'blue' ? 'bg-[#4f46e5]/10 text-[#4f46e5]' :
                                card.color === 'emerald' ? 'bg-emerald-500/10 text-emerald-500' :
                                    card.color === 'amber' ? 'bg-amber-500/10 text-amber-500' :
                                        'bg-slate-500/10 text-slate-500'
                                }`}>
                                {card.icon}
                            </div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{card.sub}</span>
                        </div>
                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{card.title}</p>
                            <p className="text-3xl font-extrabold tracking-tight">{card.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── DOMAIN CARDS GRID ── */}
            <div className="enterprise-card overflow-hidden">
                <div className="p-6 border-b border-[rgb(var(--border))] glass flex items-center justify-between">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Integrated Infrastructure</p>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">{domains.length} domains</span>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-24 space-y-4">
                        <div className="w-10 h-10 border-4 border-[#4f46e5]/20 border-t-[#4f46e5] rounded-full animate-spin" />
                        <p className="text-slate-500 font-bold tracking-widest uppercase text-xs">Synchronizing Registries...</p>
                    </div>
                ) : domains.length === 0 ? (
                    <div className="py-24 text-center text-slate-400 font-medium">No domains onboarded yet.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 p-6">
                        {domains.map(domain => (
                            <DomainCard
                                key={domain.id}
                                domain={domain}
                                onDetails={() => openDrawer(domain)}
                                onDelete={(e) => { e.stopPropagation(); setDeleteConfirm(domain); }}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* ══════════════════════════════════════════
                PORTALS
            ══════════════════════════════════════════ */}

            {/* ── ADD DOMAIN MODAL — 2-step wizard ── */}
            {modalOpen && createPortal(
                <AddDomainWizard
                    onClose={() => setModalOpen(false)}
                    onSuccess={(domain) => {
                        setModalOpen(false);
                        fetchDomains();
                    }}
                    TENANT={TENANT}
                    oauthService={adminService}
                />,
                document.body
            )}

            {/* ── DOMAIN DETAIL DRAWER ── */}
            {drawerOpen && createPortal(
                <div className="fixed inset-0 z-[99999] flex justify-end">
                    <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-lg animate-fade-in" onClick={() => setDrawerOpen(false)} />
                    <div className="relative w-full max-w-[560px] bg-[rgb(var(--card))] h-full shadow-2xl flex flex-col animate-slide-in-right border-l border-[rgb(var(--border))]">
                        {/* top accent */}
                        <div className="h-1 w-full bg-gradient-to-r from-[#4f46e5] via-indigo-400 to-[#4f46e5]" />

                        {selected && (
                            <div className="flex-1 flex flex-col overflow-hidden">

                                {/* Drawer Header */}
                                <div className="p-6 border-b border-[rgb(var(--border))] glass">
                                    <div className="flex justify-between items-center mb-5">
                                        <button
                                            onClick={() => setDrawerOpen(false)}
                                            className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-[#4f46e5] transition-all"
                                        >
                                            <ChevronRight size={20} />
                                        </button>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openDrawer(selected)}
                                                className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-[#4f46e5] transition-all"
                                            >
                                                <RefreshCcw size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Domain identity */}
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-2xl bg-[#4f46e5]/10 flex items-center justify-center text-[#4f46e5] border border-[#4f46e5]/20">
                                            <Globe size={28} strokeWidth={1.5} />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-white">{selected.name}</h2>
                                            <div className="flex items-center gap-3 mt-1">
                                                <StatusPill status={selected.status} />
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">ID: {selected.id}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tab bar */}
                                    <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl mt-5">
                                        {['Overview', 'DNS', 'Security'].map(t => (
                                            <button
                                                key={t}
                                                onClick={() => setActiveTab(t)}
                                                className={`flex-1 py-2 text-[10px] font-extrabold uppercase tracking-widest rounded-lg transition-all ${activeTab === t
                                                    ? 'bg-[#4f46e5] text-white shadow-sm'
                                                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
                                                    }`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Drawer Content */}
                                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-6">

                                    {/* ── OVERVIEW TAB ── */}
                                    {activeTab === 'Overview' && (
                                        <div className="space-y-6 animate-fade-in">
                                            {/* Quick stats */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="enterprise-card p-5">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Risk Score</p>
                                                    <p className={`text-3xl font-extrabold tracking-tight mt-1 ${selected.riskScore > 70 ? 'text-red-500' : 'text-[#4f46e5]'}`}>
                                                        {selected.riskScore}<span className="text-base font-bold text-slate-400">/100</span>
                                                    </p>
                                                </div>
                                                <div className="enterprise-card p-5">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Mailboxes</p>
                                                    <p className="text-3xl font-extrabold tracking-tight mt-1">24</p>
                                                </div>
                                            </div>

                                            {/* Config Table */}
                                            <div className="enterprise-card overflow-hidden">
                                                <div className="p-4 border-b border-[rgb(var(--border))] glass">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Infrastructure Spec</p>
                                                </div>
                                                <div className="divide-y divide-[rgb(var(--border))]">
                                                    {[
                                                        { label: 'Access Protocol', value: 'SMTP / IMAP', icon: Server },
                                                        { label: 'Sync Mode', value: selected.syncMode, icon: RefreshCcw },
                                                        { label: 'DNS Provider', value: selected.dnsProvider || 'Standard', icon: Globe },
                                                        { label: 'Encryption', value: 'TLS v1.3 / MTA-STS', icon: Lock, highlight: true },
                                                    ].map((row, i) => (
                                                        <div key={i} className="px-5 py-4 flex items-center justify-between group hover:bg-slate-50 dark:hover:bg-[#4f46e5]/5 transition-all">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-[#4f46e5] transition-colors">
                                                                    <row.icon size={16} />
                                                                </div>
                                                                <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">{row.label}</span>
                                                            </div>
                                                            <span className={`text-sm font-bold ${row.highlight ? 'text-emerald-500' : 'text-slate-900 dark:text-white'} uppercase`}>{row.value}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Advisory */}
                                            <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/20 flex items-start gap-3">
                                                <div className="p-2 bg-amber-500/10 text-amber-500 rounded-xl mt-0.5"><AlertCircle size={16} /></div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">Security Advisory</p>
                                                    <p className="text-xs text-amber-900/70 dark:text-amber-200/60 font-medium mt-1 leading-relaxed">
                                                        Verification is pending. DNS changes may take up to 24h to propagate globally.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* ── DNS TAB ── */}
                                    {activeTab === 'DNS' && (
                                        <div className="space-y-4 animate-fade-in">
                                            <div className="flex items-center justify-between">
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Required DNS Records</p>
                                                <span className="text-[10px] font-bold px-2 py-1 rounded-lg bg-[#4f46e5]/10 text-[#4f46e5] uppercase">{records.length} records</span>
                                            </div>
                                            <div className="enterprise-card overflow-hidden divide-y divide-[rgb(var(--border))]">
                                                {records.map((rec, i) => (
                                                    <div key={i} className="p-5 group hover:bg-slate-50 dark:hover:bg-[#4f46e5]/5 transition-all">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <div className="flex items-center gap-3">
                                                                <span className="px-2.5 py-1 rounded-lg bg-slate-900 dark:bg-slate-800 text-white text-[10px] font-extrabold font-mono uppercase">{rec.recordType}</span>
                                                                <span className="text-sm font-bold text-slate-900 dark:text-white">{rec.recordName}</span>
                                                            </div>
                                                            <StatusPill status={rec.status} />
                                                        </div>
                                                        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-[rgb(var(--border))]">
                                                            <code className="flex-1 text-xs text-[#4f46e5] font-mono break-all">{rec.expectedValue}</code>
                                                            <button
                                                                onClick={() => copy(rec.expectedValue, i)}
                                                                className="shrink-0 p-2 rounded-lg hover:bg-[#4f46e5]/10 text-slate-400 hover:text-[#4f46e5] transition-all"
                                                            >
                                                                {copied === i ? <CheckCircle size={14} className="text-emerald-500" /> : <Copy size={14} />}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* ── SECURITY TAB ── */}
                                    {activeTab === 'Security' && (
                                        <div className="space-y-4 animate-fade-in">
                                            <div className="enterprise-card overflow-hidden">
                                                <div className="p-4 border-b border-[rgb(var(--border))] glass">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Security Posture</p>
                                                </div>
                                                <div className="divide-y divide-[rgb(var(--border))]">
                                                    {[
                                                        { label: 'DKIM', value: 'Active', ok: true },
                                                        { label: 'SPF', value: 'Active', ok: true },
                                                        { label: 'DMARC', value: 'Pending', ok: false },
                                                        { label: 'MTA-STS', value: 'Active', ok: true },
                                                    ].map((row, i) => (
                                                        <div key={i} className="px-5 py-4 flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${row.ok ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`}>
                                                                    {row.ok ? <CheckCircle size={16} /> : <Clock size={16} />}
                                                                </div>
                                                                <span className="text-sm font-bold text-slate-900 dark:text-white">{row.label}</span>
                                                            </div>
                                                            <span className={`text-[9px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-md ${row.ok ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'}`}>
                                                                {row.value}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {/* ── DELETE CONFIRMATION MODAL ── */}
            {deleteConfirm && createPortal(
                <div className="fixed inset-0 z-[99999] flex items-center justify-center p-6">
                    <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-lg animate-fade-in" onClick={() => !deleting && setDeleteConfirm(null)} />
                    <div className="relative w-full max-w-md bg-[rgb(var(--card))] rounded-3xl shadow-2xl border border-[rgb(var(--border))] overflow-hidden animate-scale-in">
                        <div className="h-1 w-full bg-gradient-to-r from-red-500 via-red-400 to-red-500" />
                        <div className="p-8 space-y-6">
                            <div className="flex items-start gap-4">
                                <div className="p-3 rounded-2xl bg-red-500/10 text-red-500 shrink-0">
                                    <Trash2 size={24} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-white">Decommission Domain?</h2>
                                    <p className="text-sm text-slate-500 font-medium mt-1">This action is permanent and cannot be undone.</p>
                                </div>
                            </div>
                            <div className="p-4 rounded-2xl bg-red-500/5 border border-red-500/20">
                                <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-1">Target Asset</p>
                                <p className="text-base font-extrabold text-slate-900 dark:text-white">{deleteConfirm.name}</p>
                                <p className="text-xs text-slate-500 font-medium mt-1">All associated DNS records will also be permanently removed.</p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setDeleteConfirm(null)}
                                    disabled={deleting}
                                    className="flex-1 py-3 border border-[rgb(var(--border))] rounded-xl text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-bold shadow-sm shadow-red-500/25 transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
                                >
                                    {deleting ? (
                                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Removing...</>
                                    ) : (
                                        <><Trash2 size={16} />Decommission</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

/* ─────────────────────────────────────────────
   Domain Card — same padding/font scale as UserManagement rows
───────────────────────────────────────────── */
function DomainCard({ domain, onDetails, onDelete }) {
    return (
        <div
            onClick={onDetails}
            className="enterprise-card p-6 group cursor-pointer hover:-translate-y-0.5 transition-all duration-200"
        >
            <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 rounded-2xl bg-[#4f46e5]/10 flex items-center justify-center text-[#4f46e5] border border-[#4f46e5]/20 group-hover:scale-110 group-hover:bg-[#4f46e5] group-hover:text-white transition-all duration-300">
                    <Globe size={22} strokeWidth={1.5} />
                </div>
                <div className="flex items-center gap-2">
                    <StatusPill status={domain.status} />
                    <button
                        onClick={onDelete}
                        title="Delete domain"
                        className="p-2 rounded-xl text-slate-300 hover:text-red-500 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100 active:scale-90"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            <div className="space-y-1 mb-5">
                <h3 className="text-base font-extrabold tracking-tight text-slate-900 dark:text-white group-hover:text-[#4f46e5] transition-colors">{domain.name}</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                    <Cpu size={10} />
                    {domain.syncMode} Verification
                </p>
            </div>

            <div className="pt-4 border-t border-[rgb(var(--border))] grid grid-cols-2 gap-4">
                <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Risk Score</p>
                    <p className={`text-lg font-extrabold tracking-tight ${domain.riskScore > 70 ? 'text-red-500' : 'text-[#4f46e5]'}`}>
                        {domain.riskScore}<span className="text-sm text-slate-400">/100</span>
                    </p>
                </div>
                <div className="text-right">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Provider</p>
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase mt-0.5">
                        {DNS_DISPLAY_NAMES[domain.dnsProvider] || domain.dnsProvider || 'Standard'}
                    </p>
                </div>
            </div>
        </div>
    );
}

function FormField({ label, children }) {
    return (
        <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</label>
            {children}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────────────────
   2-Step Add Domain Wizard
───────────────────────────────────────────────────────────────────────────── */

const OAUTH_PROVIDERS = {
    CLOUDFLARE: { name: 'Cloudflare', color: 'bg-orange-500', icon: '🟠', oauth: true },
    GODADDY: { name: 'GoDaddy', color: 'bg-green-600', icon: '🟢', oauth: true },
    GOOGLE_CLOUD: { name: 'Google Cloud', color: 'bg-blue-500', icon: '🔵', oauth: false },
    AZURE_DNS: { name: 'Azure DNS', color: 'bg-blue-700', icon: '🔷', oauth: false },
    AWS_ROUTE53: { name: 'AWS Route 53', color: 'bg-yellow-500', icon: '🟡', oauth: false },
    DIGITALOCEAN: { name: 'DigitalOcean', color: 'bg-cyan-500', icon: '🔹', oauth: false },
    VERCEL: { name: 'Vercel', color: 'bg-slate-900', icon: '▲', oauth: false },
    NAMECHEAP: { name: 'Namecheap', color: 'bg-red-500', icon: '🔴', oauth: false },
    PORKBUN: { name: 'Porkbun', color: 'bg-pink-500', icon: '🐷', oauth: false },
    VULTR: { name: 'Vultr', color: 'bg-indigo-500', icon: '🔷', oauth: false },
    LINODE: { name: 'Linode', color: 'bg-green-500', icon: '🟩', oauth: false },
};

const DNS_STEPS = [
    { label: 'MX Record', sub: 'Mail exchange routing' },
    { label: 'SPF Record', sub: 'Sender policy framework' },
    { label: 'DKIM Record', sub: 'DomainKeys identified mail' },
    { label: 'DMARC Record', sub: 'Domain message auth' },
];

function AddDomainWizard({ onClose, onSuccess, TENANT, oauthService }) {
    const [step, setStep] = useState(1);   // 1 = domain info, 2 = provider, 3 = credential form
    const [syncMode, setSyncMode] = useState('MANUAL');
    const [provider, setProvider] = useState('NONE');
    const [domainName, setDomainName] = useState('');
    const [createdDomain, setCreatedDomain] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [oauthLoading, setOauthLoading] = useState(false);
    // Credential form state (for API-key providers like GoDaddy)
    const [credentialStep, setCredentialStep] = useState(false);
    const [credentialHint, setCredentialHint] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [apiSecret, setApiSecret] = useState('');
    const [credConnecting, setCredConnecting] = useState(false);

    const provInfo = OAUTH_PROVIDERS[provider];

    async function handleStep1(e) {
        e.preventDefault();
        if (!domainName.trim()) return;
        setStep(2);
    }

    async function submitManual() {
        setSubmitting(true);
        try {
            const domain = await adminService.createDomain({
                name: domainName,
                tenantId: TENANT,
                syncMode,
                provider: provider || 'NONE',
            });
            onSuccess(domain);
        } catch (err) {
            alert(`Provisioning error: ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    }

    async function handleOAuthConnect() {
        setOauthLoading(true);
        try {
            // Step 1: Create the domain record
            const domain = await adminService.createDomain({
                name: domainName,
                tenantId: TENANT,
                syncMode: 'AUTOMATIC',
                provider,
            });
            setCreatedDomain(domain);

            // Step 2: Ask backend what flow this provider needs
            const result = await oauthService.getOAuthUrl(domain.id, provider);

            if (result.requiresApiCredentials) {
                // Providers like GoDaddy: show a form to enter API Key + Secret
                setCredentialHint(result.credentialHint || '');
                setCredentialStep(true);
                setOauthLoading(false);
            } else {
                // Standard OAuth: redirect browser to provider login
                window.location.href = result.url;
            }
        } catch (err) {
            alert(`OAuth initiation failed: ${err.message}`);
            setOauthLoading(false);
        }
    }

    async function handleCredentialSubmit() {
        if (!apiKey.trim()) {
            alert('Please enter your API Key');
            return;
        }
        const credential = apiSecret.trim() ? `${apiKey.trim()}:${apiSecret.trim()}` : apiKey.trim();
        setCredConnecting(true);
        try {
            await oauthService.connectWithCredentials(createdDomain.id, credential);
            onSuccess(createdDomain);
        } catch (err) {
            alert(`Connection failed: ${err.message}`);
        } finally {
            setCredConnecting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-lg animate-fade-in" onClick={onClose} />
            <div className="relative w-full max-w-lg bg-[rgb(var(--card))] rounded-3xl shadow-2xl border border-[rgb(var(--border))] overflow-hidden animate-scale-in">

                {/* top accent */}
                <div className="h-1 w-full bg-gradient-to-r from-[#4f46e5] via-indigo-400 to-[#4f46e5]" />

                {/* step indicator */}
                <div className="px-8 pt-6 flex items-center gap-3">
                    {[1, 2].map(n => (
                        <div key={n} className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold transition-all ${step >= n || credentialStep ? 'bg-[#4f46e5] text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>{n}</div>
                            {n < 2 && <div className={`flex-1 h-0.5 w-8 rounded-full ${step > n || credentialStep ? 'bg-[#4f46e5]' : 'bg-slate-200 dark:bg-slate-700'}`} />}
                        </div>
                    ))}
                    <span className="ml-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        {credentialStep ? 'API Credentials' : step === 1 ? 'Domain Info' : 'Provider Connection'}
                    </span>
                    <button onClick={onClose} className="ml-auto p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-red-500 transition-all">
                        <XCircle size={18} />
                    </button>
                </div>

                <div className="p-8 pt-5 space-y-6">

                    {/* ── STEP 1: Domain + Sync Mode ── */}
                    {step === 1 && (
                        <form className="space-y-5" onSubmit={handleStep1}>
                            <div>
                                <p className="text-[10px] font-bold text-[#4f46e5] uppercase tracking-widest flex items-center gap-2 mb-1">
                                    <Database size={12} /> Asset Provisioning
                                </p>
                                <h2 className="text-2xl font-extrabold tracking-tight">Onboard Domain</h2>
                                <p className="text-sm text-slate-500 mt-1 font-medium">Register a new FQDN to the infrastructure registry.</p>
                            </div>
                            <FormField label="Domain / FQDN">
                                <input
                                    value={domainName}
                                    onChange={e => setDomainName(e.target.value)}
                                    required
                                    placeholder="example.com"
                                    className="premium-input"
                                />
                            </FormField>
                            <FormField label="Sync Mode">
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { val: 'MANUAL', label: 'Manual', desc: 'Configure DNS records yourself' },
                                        { val: 'AUTOMATIC', label: 'Automatic', desc: 'OAuth-connected auto sync' }
                                    ].map(opt => (
                                        <button
                                            key={opt.val}
                                            type="button"
                                            onClick={() => setSyncMode(opt.val)}
                                            className={`p-4 rounded-2xl border-2 text-left transition-all ${syncMode === opt.val
                                                ? 'border-[#4f46e5] bg-[#4f46e5]/5'
                                                : 'border-[rgb(var(--border))] hover:border-[#4f46e5]/40'}`}
                                        >
                                            <p className={`text-sm font-extrabold ${syncMode === opt.val ? 'text-[#4f46e5]' : 'text-slate-900 dark:text-white'}`}>{opt.label}</p>
                                            <p className="text-[10px] font-medium text-slate-400 mt-0.5">{opt.desc}</p>
                                        </button>
                                    ))}
                                </div>
                            </FormField>
                            <button type="submit" className="w-full py-3 bg-[#4f46e5] hover:bg-[#3558cc] text-white font-bold text-sm rounded-xl shadow-sm shadow-[#4f46e5]/25 transition-all active:scale-[0.98]">
                                Continue →
                            </button>
                        </form>
                    )}

                    {/* ── STEP 2: Provider + OAuth / Manual Submit ── */}
                    {step === 2 && !credentialStep && (
                        <div className="space-y-6">
                            <div>
                                <button onClick={() => setStep(1)} className="text-[10px] font-bold text-slate-400 hover:text-[#4f46e5] uppercase tracking-widest flex items-center gap-1 mb-3 transition-colors">
                                    ← Back
                                </button>
                                <h2 className="text-2xl font-extrabold tracking-tight">
                                    {syncMode === 'AUTOMATIC' ? 'Connect DNS Provider' : 'Select DNS Provider'}
                                </h2>
                                <p className="text-sm text-slate-500 mt-1 font-medium">
                                    {syncMode === 'AUTOMATIC'
                                        ? 'Authorize via OAuth to enable automatic DNS record creation.'
                                        : 'Select your registrar for manual DNS record setup.'}
                                </p>
                            </div>

                            {/* Domain chip */}
                            <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-[rgb(var(--border))]">
                                <Globe size={16} className="text-[#4f46e5]" />
                                <span className="text-sm font-bold text-slate-900 dark:text-white">{domainName}</span>
                                <span className={`ml-auto text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-md ${syncMode === 'AUTOMATIC' ? 'bg-[#4f46e5]/10 text-[#4f46e5]' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                                    {syncMode}
                                </span>
                            </div>

                            {/* Provider grid */}
                            <div className="grid grid-cols-3 gap-2 max-h-52 overflow-y-auto pr-1">
                                {[{ key: 'NONE', name: 'Standard', icon: '🔧', oauth: false },
                                ...Object.entries(OAUTH_PROVIDERS).map(([k, v]) => ({ key: k, ...v }))
                                ].map(p => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        onClick={() => setProvider(p.key)}
                                        className={`p-3 rounded-2xl border-2 text-center transition-all flex flex-col items-center gap-1.5 ${provider === p.key
                                            ? 'border-[#4f46e5] bg-[#4f46e5]/5'
                                            : 'border-[rgb(var(--border))] hover:border-[#4f46e5]/40'}`}
                                    >
                                        <span className="text-xl">{p.icon || '🌐'}</span>
                                        <span className={`text-[9px] font-extrabold leading-tight ${provider === p.key ? 'text-[#4f46e5]' : 'text-slate-600 dark:text-slate-300'}`}>
                                            {p.name}
                                        </span>
                                        {syncMode === 'AUTOMATIC' && p.oauth && (
                                            <span className="text-[8px] font-bold text-emerald-500 uppercase tracking-widest">OAuth</span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* OAuth connect OR Manual submit */}
                            {syncMode === 'AUTOMATIC' && provInfo?.oauth ? (
                                <div className="space-y-3">
                                    <div className="p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 space-y-2">
                                        <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">What Happens Next</p>
                                        {['Redirect to ' + provInfo.name + ' login', 'You authenticate (+ OTP if enabled)', 'Portal receives access token', 'MX, SPF, DKIM, DMARC created automatically', 'Domain marked ✔ Verified'].map((s, i) => (
                                            <div key={i} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium">
                                                <span className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-500 flex items-center justify-center text-[9px] font-bold shrink-0">{i + 1}</span>
                                                {s}
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        onClick={handleOAuthConnect}
                                        disabled={oauthLoading || provider === 'NONE'}
                                        className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold text-sm rounded-xl shadow-sm shadow-emerald-500/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                                    >
                                        {oauthLoading ? (
                                            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Preparing...</>
                                        ) : (
                                            <><Lock size={16} />Connect {provInfo?.name || 'Provider'} via OAuth</>
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={submitManual}
                                    disabled={submitting}
                                    className="w-full py-3 bg-[#4f46e5] hover:bg-[#3558cc] disabled:opacity-60 text-white font-bold text-sm rounded-xl shadow-sm shadow-[#4f46e5]/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                                >
                                    {submitting ? (
                                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Provisioning...</>
                                    ) : 'Initialize Infrastructure'}
                                </button>
                            )}
                        </div>
                    )}

                    {/* ── STEP 3: API Credentials Form (for GoDaddy-style providers) ── */}
                    {credentialStep && (
                        <div className="space-y-5 animate-fade-in">
                            <div>
                                <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest flex items-center gap-2 mb-1">
                                    <Lock size={12} /> API Key Authentication
                                </p>
                                <h2 className="text-2xl font-extrabold tracking-tight">Enter {provInfo?.name || 'Provider'} Credentials</h2>
                                <p className="text-sm text-slate-500 mt-1 font-medium">
                                    {provInfo?.name} uses API keys instead of browser login. Enter your developer credentials below.
                                </p>
                            </div>

                            {/* Domain chip */}
                            <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-[rgb(var(--border))]">
                                <Globe size={16} className="text-amber-500" />
                                <span className="text-sm font-bold text-slate-900 dark:text-white">{domainName}</span>
                                <span className="ml-auto text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-500">
                                    {provInfo?.name}
                                </span>
                            </div>

                            {/* Help box */}
                            <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/20 space-y-1">
                                <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest">How to get your API Key</p>
                                <p className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                                    1. Go to <a href="https://developer.godaddy.com/keys" target="_blank" rel="noreferrer" className="text-amber-500 underline hover:text-amber-400">developer.godaddy.com/keys</a><br />
                                    2. Click "Create New API Key"<br />
                                    3. Select <strong>Production</strong> environment<br />
                                    4. Copy the Key and Secret below
                                </p>
                            </div>

                            {/* API Key input */}
                            <FormField label="API Key">
                                <input
                                    value={apiKey}
                                    onChange={e => setApiKey(e.target.value)}
                                    placeholder="3mXabc123DefGhi..."
                                    className="premium-input font-mono text-sm"
                                    autoComplete="off"
                                />
                            </FormField>

                            {/* API Secret input */}
                            <FormField label="API Secret">
                                <input
                                    type="password"
                                    value={apiSecret}
                                    onChange={e => setApiSecret(e.target.value)}
                                    placeholder="SecretKeyHere..."
                                    className="premium-input font-mono text-sm"
                                    autoComplete="off"
                                />
                            </FormField>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setCredentialStep(false)}
                                    className="flex-1 py-3 border border-[rgb(var(--border))] hover:border-[#4f46e5]/40 text-slate-600 dark:text-slate-300 font-bold text-sm rounded-xl transition-all"
                                >
                                    ← Back
                                </button>
                                <button
                                    onClick={handleCredentialSubmit}
                                    disabled={credConnecting || !apiKey.trim()}
                                    className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold text-sm rounded-xl shadow-sm shadow-amber-500/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                                >
                                    {credConnecting ? (
                                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Connecting...</>
                                    ) : (
                                        <><Lock size={16} />Connect & Setup DNS</>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
